import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { TextField } from "@material-ui/core";
import styled from "styled-components";
import Button from "components/button/Button";
import { postUpdate, fetchCampaign, editUpdate, deleteUpdate } from "./fundraisingSlice";
import { formatDate } from "helper/dateFormat";
import { fontConfig } from "theme/fontConfig";

const ComposeCard = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 20px 24px;
  margin-bottom: 24px;
`;

const ComposeTitle = styled.div`
  font-family: var(--FundraisingComposeTitleFont-family);
  font-size: 13px;
  font-weight: 600;
  color: #002e5c;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  opacity: 0.7;
  margin-bottom: 14px;
`;

const ComposeRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StatusMsg = styled.div`
  font-family: var(--FundraisingStatusMsgFont-family);
  font-size: 13px;
  margin-bottom: 10px;
  color: ${({ error }) => error ? "#fa3372" : "#28c238"};
`;

const FeedTitle = styled.div`
  font-family: var(--FundraisingFeedTitleFont-family);
  font-size: 13px;
  font-weight: 600;
  color: #002e5c;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  opacity: 0.7;
  margin-bottom: 16px;
`;

const UpdateCard = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 18px 20px;
  margin-bottom: 10px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
`;

const UpdateMeta = styled.div`
  font-family: var(--FundraisingUpdateMetaFont-family);
  font-size: 12px;
  color: #9ca3af;
  margin-bottom: 4px;
`;

const UpdateTitle = styled.div`
  font-family: var(--FundraisingUpdateTitleFont-family);
  font-size: 15px;
  font-weight: 600;
  color: #002e5c;
  margin-bottom: 6px;
`;

const UpdateBody = styled.div`
  font-family: var(--FundraisingUpdateBodyFont-family);
  font-size: 13px;
  color: #4d5565;
  line-height: 1.7;
`;

const ActionBtns = styled.div`
  display: flex;
  gap: 4px;
  flex-shrink: 0;
  align-self: flex-start;
`;

const IconBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-family: var(--FundraisingIconBtnFont-family);
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 5px;
  color: ${({ danger }) => danger ? "#fa3372" : "#2555ef"};
  &:hover { background: ${({ danger }) => danger ? "rgba(250,51,114,.08)" : "rgba(37,85,239,.08)"}; }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 0;
  font-family: var(--FundraisingEmptyStateFont-family);
  color: #9ca3af;
  font-size: 14px;
`;

const inputStyle = {
  style: { backgroundColor: "#fff", borderRadius: 6, fontFamily: fontConfig.FundraisingInputFont.family, fontSize: 14 },
};

const schema = Yup.object({
  title: Yup.string().required("Title is required"),
  body:  Yup.string().required("Message is required"),
});

const fmtDate = d => formatDate({ date: d });

const UpdateForm = ({ campaignId }) => {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { analytics, currentCampaign } = useSelector(s => s.fundraising);
  const [editingId, setEditingId] = useState(null);

  const updates = (currentCampaign?.updates ?? analytics?.updates ?? [])
    .slice()
    .reverse();

  useEffect(() => {
    const load = async () => {
      const token = await getAccessTokenSilently();
      dispatch(fetchCampaign({ id: campaignId, token }));
    };
    load();
  }, [campaignId]); // eslint-disable-line react-hooks/exhaustive-deps

  const editingUpdate = editingId ? updates.find(u => u._id === editingId) : null;

  const handleSubmit = async (values, { setSubmitting, resetForm, setStatus }) => {
    try {
      const token = await getAccessTokenSilently();
      if (editingId) {
        await dispatch(editUpdate({ campaignId, updateId: editingId, data: values, token }));
        setEditingId(null);
        setStatus({ success: "Update saved!" });
      } else {
        await dispatch(postUpdate({ id: campaignId, data: values, token }));
        setStatus({ success: "Update posted!" });
      }
      resetForm();
    } catch {
      setStatus({ error: "Something went wrong." });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (updateId) => {
    if (!window.confirm("Delete this update?")) return;
    const token = await getAccessTokenSilently();
    dispatch(deleteUpdate({ campaignId, updateId, token }));
  };

  return (
    <div>
      {/* Compose bar */}
      <ComposeCard>
        <ComposeTitle>{editingId ? "Edit Update" : "Post Campaign Update"}</ComposeTitle>
        <Formik
          enableReinitialize
          initialValues={{
            title: editingUpdate?.title || "",
            body:  editingUpdate?.body  || "",
          }}
          validationSchema={schema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, isSubmitting, status }) => (
            <Form>
              {status?.success && <StatusMsg>{status.success}</StatusMsg>}
              {status?.error   && <StatusMsg error>{status.error}</StatusMsg>}
              <ComposeRow>
                <Field
                  as={TextField}
                  name="title"
                  label="Update Title *"
                  fullWidth
                  variant="outlined"
                  size="small"
                  error={touched.title && !!errors.title}
                  helperText={touched.title && errors.title}
                  InputProps={inputStyle}
                />
                <Field
                  as={TextField}
                  name="body"
                  label="Message *"
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                  size="small"
                  error={touched.body && !!errors.body}
                  helperText={touched.body && errors.body}
                  InputProps={inputStyle}
                />
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                  {editingId && (
                    <Button variant="secondary" size="md" type="button" onClick={() => setEditingId(null)}>
                      Cancel
                    </Button>
                  )}
                  <Button variant="primary" size="md" type="submit" disabled={isSubmitting}>
                    {isSubmitting ? "Saving…" : editingId ? "Save" : "Post Update"}
                  </Button>
                </div>
              </ComposeRow>
            </Form>
          )}
        </Formik>
      </ComposeCard>

      {/* Updates feed */}
      <FeedTitle>Posted Updates ({updates.length})</FeedTitle>
      {updates.length === 0 ? (
        <EmptyState>No updates posted yet.</EmptyState>
      ) : (
        updates.map((u, i) => (
          <UpdateCard key={u._id || i}>
            <div>
              <UpdateMeta>{fmtDate(u.createdAt || u.sent_at || Date.now())}</UpdateMeta>
              <UpdateTitle>{u.title}</UpdateTitle>
              {u.body && <UpdateBody>{u.body}</UpdateBody>}
            </div>
            <ActionBtns>
              <IconBtn type="button" onClick={() => setEditingId(u._id)}>Edit</IconBtn>
              <IconBtn type="button" danger="true" onClick={() => handleDelete(u._id)}>Delete</IconBtn>
            </ActionBtns>
          </UpdateCard>
        ))
      )}
    </div>
  );
};

export default UpdateForm;
